<script setup lang="ts">
import type { Editor, PluginManager } from 'tinymce';
import { AksInsertCtaPlugin } from './aks-insert-cta-plugin';
import { AksInsertLinkPlugin } from './aks-insert-link-plugin';
import { AksInsertImagePlugin } from './aks-insert-image-plugin';
import { AksInsertProductCardPlugin } from './aks-insert-product-card-plugin';
import { AksInsertAiContentPlugin } from './aks-insert-ai-content-plugin';
import { computed } from 'vue';
import { toRefs } from 'vue';
import { EditorInteractionManager, MediaLibraryServiceComposable, TrackingServiceComposable, ImageCdnService, UseDrawerComposableService } from './types';
import { createEditorInteractionManager } from './utilities';
import { CaptureErrorFunc } from '../use-error-logger';

defineOptions({ name: 'AksPlugins' });

const props = defineProps<{
  pluginManager: PluginManager;
  id: string;
  editor: Editor;
  useMediaLibraryService: MediaLibraryServiceComposable;
  useTrackingService: TrackingServiceComposable;
  imageCdnService: ImageCdnService;
  useDrawerService: UseDrawerComposableService;
  captureError: CaptureErrorFunc;
}>();

const emit = defineEmits(['allContentUpdated']);

const { pluginManager, id, editor, useMediaLibraryService, useTrackingService, imageCdnService, useDrawerService, captureError } = toRefs(props);
const { trackUserClick } = useTrackingService.value();

// An adapter layer to manage interactions between our plugins, and the TinyMCE editor.
const editorInteractionManager = computed<EditorInteractionManager>(() => createEditorInteractionManager(pluginManager.value, editor.value, captureError.value));

const handleToolbarButtonClicked = (pluginKey: string) => {
  trackUserClick({
    component: 'rich_text_editor',
    action: 'plugin_toolbar_button_clicked',
    id_section: pluginKey,
    // Indicates which editor instance spawned this event
    page_title: id.value,
  });
};

const handleContentInserted = (pluginKey: string) => {
  trackUserClick({
    component: 'rich_text_editor',
    action: 'plugin_content_insertion_confirmed',
    id_section: pluginKey,
    // Indicates which editor instance spawned this event
    page_title: id.value,
  });
};

const handleAiContentInserted = (content: string, pluginKey: string) => {
  handleContentInserted(pluginKey)
  emit('allContentUpdated', content);
};

const commonProps = {
  id: id.value,
  captureError: props.captureError,
  editorInteractionManager: editorInteractionManager.value,
};

</script>
<template>
  <AksInsertCtaPlugin
    v-bind="commonProps"
    @toolbar-button-clicked="handleToolbarButtonClicked"
    @content-inserted="handleContentInserted"
  />
  <AksInsertLinkPlugin
    v-bind="commonProps"
    @toolbar-button-clicked="handleToolbarButtonClicked"
    @content-inserted="handleContentInserted"
  />
  <AksInsertImagePlugin
    v-bind="commonProps"
    :use-media-library-service="useMediaLibraryService"
    @toolbar-button-clicked="handleToolbarButtonClicked"
    @content-inserted="handleContentInserted"
  />
  <AksInsertProductCardPlugin
    v-bind="commonProps"
    :image-cdn-service="imageCdnService"
    :use-drawer-service="useDrawerService"
    @toolbar-button-clicked="handleToolbarButtonClicked"
    @content-inserted="handleContentInserted"
  />
  <AksInsertAiContentPlugin
    v-bind="commonProps"
    :image-cdn-service="imageCdnService"
    :use-drawer-service="useDrawerService"
    @toolbar-button-clicked="handleToolbarButtonClicked"
    @content-inserted="handleAiContentInserted"
  />
  <!-- More plugin UI can be added here -->
</template>

<style lang="scss">
.rich-text-editor {
  // Custom styling for AKS toolbar
  .tox .tox-tbtn__select-label {
    @apply ds-font-bold ds-text-xs ds-text-primary ds-uppercase;
  }

  // Remove style that interferes with our use of AKS icons
  .tox .tox-tbtn svg.aks-icon {
    fill: transparent;
  }
}

// Custom styling for AKS context menu items
.tox .tox-collection__item-label:not(svg):not(rect) {
  font-family: 'Poppins', sans-serif;
}

// Ensure that toolbars and context menus do not appear above Ankorstore modals
.tox.tox-tinymce-aux {
  z-index: 1;
}
</style>
